import { KEEP_PLACE } from "@/lib/search";
import { errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "@/lib/icons";
import { useState } from "react";
import type { PaginationState } from "@tanstack/react-table";
import { toast } from "sonner";
import type { ResidentListItem } from "@alm/api-contracts";
import { BulkBar } from "@/components/app/lists/bulk-bar";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { saveFile } from "@/lib/files/download";
import { useFacility } from "@/lib/facility/facility";
import { RemoveConfirm } from "@/components/app/fields/remove-confirm";
import { ResidentWelcomeOverlay } from "@/components/app/celebration/resident-welcome-overlay";
import { orpc, type Output, orpcClient } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { ResidentEditDialog } from "./-components/resident-edit-dialog";
import { formToInput, ResidentForm } from "./-components/resident-form";
import { ResidentHeaderActions } from "./-components/resident-header-actions";
import { ResidentMergeDialog } from "./-components/resident-merge-dialog";
import {
  hasNarrowing,
  parseResidentSearch,
  type ResidentSearch,
  type SortKey,
} from "./-components/resident-search";
import { ResidentTable } from "./-components/resident-table";
import { TableCard } from "@/components/app/patterns/table-card";
import { keepWhileSame } from "@/lib/query/keep-while-same";
import { ResidentToolbar } from "./-components/resident-toolbar";

export const Route = createFileRoute("/_authenticated/_care/residents/")({
  validateSearch: parseResidentSearch,
  component: ResidentsPage,
});

const fail = (error: Error) => toast.error(errorWords(error));

function ResidentsPage() {
  const { facilityId } = useFacility();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const canCreate = useCan("resident:create");
  const canUpdate = useCan("resident:update");
  const canDelete = useCan("resident:delete");
  const canMerge = useCan("resident:merge");
  const canExport = useCan("resident:export");

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<ResidentListItem | null>(null);
  const [merging, setMerging] = useState<{ preset?: [string, string] } | null>(null);
  const [welcoming, setWelcoming] = useState<Output<
    typeof orpcClient.care.residents.create
  > | null>(null);

  const page = search.page ?? 1;
  const pageSize = search.pageSize ?? 25;
  const input = {
    facilityId,
    search: search.q?.trim(),
    status: search.status?.length ? search.status : ("all" as const),
    gender: search.gender,
    payerType: search.payer,
    room: search.room,
    minAge: search.minAge,
    maxAge: search.maxAge,
    tagIds: search.tags ?? [],
    sort: search.sort ?? "name",
    dir: search.dir ?? "asc",
    limit: pageSize,
    offset: (page - 1) * pageSize,
  };
  const list = useQuery({
    ...orpc.care.residents.list.queryOptions({ input }),
    placeholderData: keepWhileSame({ facilityId }),
  });
  const facilities = useQuery(
    orpc.administration.facilities.list.queryOptions({ input: { includeInactive: false } }),
  );
  const tags = useQuery(
    orpc.administration.catalogs.tags.list.queryOptions({ input: { facilityId } }),
  );

  const invalidate = () => queryClient.invalidateQueries({ queryKey: orpc.care.residents.key() });
  const create = useMutation(
    orpc.care.residents.create.mutationOptions({
      onSuccess: async (created) => {
        toast.success("Resident added");
        setCreating(false);
        await invalidate();
        setWelcoming(created);
      },
      onError: fail,
    }),
  );
  const remove = useMutation(
    orpc.care.residents.remove.mutationOptions({
      onSuccess: async () => {
        toast.success("Resident removed");
        setDeleting(null);
        await invalidate();
      },
      onError: fail,
    }),
  );
  const exporting = useMutation(
    orpc.care.residents.export.mutationOptions({ onSuccess: saveFile, onError: fail }),
  );

  const update = (patch: Partial<ResidentSearch>) =>
    navigate({
      search: (old) => ({ ...old, ...patch, page: undefined }),
      ...KEEP_PLACE,
    });
  const sortBy = (key: SortKey, dir: "asc" | "desc") => update({ sort: key, dir });
  const goToPage = (next: PaginationState) =>
    next.pageSize !== pageSize
      ? update({ pageSize: next.pageSize === 25 ? undefined : next.pageSize })
      : navigate({
          search: (old) => ({ ...old, page: next.pageIndex > 0 ? next.pageIndex + 1 : undefined }),
        });
  const clearFilters = () =>
    navigate({ search: { sort: search.sort, dir: search.dir, cols: search.cols }, replace: true });

  const rows = list.data?.rows ?? [];
  const total = list.data?.total ?? 0;
  const everyone = list.data ? Object.values(list.data.counts).reduce((a, b) => a + b, 0) : 0;
  const toggleAll = () => {
    const ids = rows.map((row) => row.id);
    const all = ids.every((id) => selectedIds.includes(id));
    setSelectedIds((prev) =>
      all ? prev.filter((id) => !ids.includes(id)) : [...new Set([...prev, ...ids])],
    );
  };
  const rowProps = {
    rows,
    total,
    search,
    showFacility: facilityId === "all",
    selectedIds,
    onToggle: (id: string) =>
      setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])),
    onToggleAll: toggleAll,
    onSort: sortBy,
    onPaging: goToPage,
    canUpdate,
    canDelete,
    onEdit: (resident: ResidentListItem) => setEditingId(resident.id),
    onDelete: setDeleting,
  };
  const exportRows = (format: "csv" | "pdf", ids?: string[]) => {
    const residentIds = ids ?? rows.map((row) => row.id);
    if (residentIds.length === 0) return;
    exporting.mutate({ residentIds, format });
  };

  const headerActions = {
    canExport,
    exporting: exporting.isPending,
    onExport: (kind: "csv" | "pdf") => exportRows(kind),
    canMerge,
    onMerge: () => setMerging({}),
  };

  return (
    <>
      <PageHeader
        title="Residents"
        count={list.data ? everyone : undefined}
        meta={
          /* On a phone Export and Merge sit small at the end of this line, beside the count,
             instead of a row of their own; a desk keeps them in the header's actions. */
          <span className="max-md:flex max-md:items-center max-md:justify-between max-md:gap-2">
            <span>
              {list.data
                ? `${list.data.counts.active ?? 0} active${
                    total !== everyone ? `, ${total} match these filters` : ""
                  }`
                : "Loading residents…"}
            </span>
            <span className="hidden shrink-0 items-center gap-1.5 max-md:flex">
              <ResidentHeaderActions {...headerActions} compact />
            </span>
          </span>
        }
        actions={
          <>
            {/* On a phone these sit beside the count instead (the meta line above). */}
            <span className="contents max-md:hidden">
              <ResidentHeaderActions {...headerActions} />
            </span>
            {canCreate && (
              <Button onClick={() => setCreating(true)}>
                <Plus className="size-4" />
                Add resident
              </Button>
            )}
          </>
        }
      />

      <ResidentToolbar
        search={search}
        counts={list.data?.counts}
        tags={tags.data ?? []}
        onChange={update}
        onClear={clearFilters}
      />

      <TableCard busy={list.isPlaceholderData}>
        <BulkBar count={selectedIds.length} noun="residents" onClear={() => setSelectedIds([])}>
          {canExport && (
            <Button
              variant="outline"
              size="sm"
              disabled={exporting.isPending}
              onClick={() => exportRows("csv", selectedIds)}
            >
              Export selected
            </Button>
          )}
          {canMerge && (
            <Button
              variant="outline"
              size="sm"
              disabled={selectedIds.length !== 2}
              onClick={() => setMerging({ preset: [selectedIds[0]!, selectedIds[1]!] })}
            >
              Merge these two
            </Button>
          )}
        </BulkBar>

        {list.isPending ? (
          <div className="flex flex-col gap-3 p-4" aria-busy="true">
            {["a", "b", "c", "d", "e"].map((slot) => (
              <Skeleton key={slot} className="h-12 w-full" />
            ))}
          </div>
        ) : list.isError ? (
          <ReadFailed
            className="p-4"
            message="Could not load residents."
            error={list.error}
            onRetry={() => void list.refetch()}
            retrying={list.isFetching}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            title={hasNarrowing(search) ? "No residents match these filters" : "No residents yet"}
            description={
              hasNarrowing(search)
                ? "Widen the search or clear a filter to see more."
                : "Add the first resident to start tracking care, billing and documents."
            }
            action={
              hasNarrowing(search) ? (
                <Button variant="outline" onClick={clearFilters}>
                  Clear filters
                </Button>
              ) : canCreate ? (
                <Button variant="outline" onClick={() => setCreating(true)}>
                  Add resident
                </Button>
              ) : undefined
            }
          />
        ) : (
          <ResidentTable {...rowProps} />
        )}
      </TableCard>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add a resident</DialogTitle>
          </DialogHeader>
          <ResidentForm
            facilities={facilities.data ?? []}
            defaultValues={facilityId !== "all" ? { facilityId } : undefined}
            pending={create.isPending}
            submitLabel="Add resident"
            onCancel={() => setCreating(false)}
            onSubmit={(values) => create.mutate(formToInput(values))}
          />
        </DialogContent>
      </Dialog>

      <ResidentEditDialog
        residentId={editingId}
        facilities={facilities.data ?? []}
        onClose={() => setEditingId(null)}
      />

      {merging && (
        <ResidentMergeDialog
          open
          residents={rows}
          preset={merging.preset}
          onOpenChange={(open) => !open && setMerging(null)}
        />
      )}

      {deleting ? (
        <RemoveConfirm
          title={`Delete ${deleting.firstName} ${deleting.lastName}?`}
          description="Their chart and everything attached to it will be removed. This cannot be undone."
          confirmLabel="Delete resident"
          onConfirm={() => remove.mutate({ residentId: deleting.id })}
          onClose={() => setDeleting(null)}
        />
      ) : null}
      {welcoming ? (
        <ResidentWelcomeOverlay resident={welcoming} onClose={() => setWelcoming(null)} />
      ) : null}
    </>
  );
}
